import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { api, investorApi, kycApi } from '@/api';
import { investorActions } from '@/store/investor';
import { useDispatch } from 'react-redux';
import { Button } from '@/components';
import { Container, Link, Flex } from '@/ui';
import { CountrySelect, PhoneInput } from '@/modules/kycModule/Fields';
import {
  API_URL,
  EMAIL_REGEX,
  PASSWORD_REGEX,
  CYRILLIC_LATIN_REGEX,
  ONLY_LATIN_REGEX
} from '@/utils/static';
import { useCountries, useGetStatics, usePopups } from '@/hooks';
import { useForm } from 'react-hook-form';
import { FormInput } from '@/modules/kycModule/components';
import { useTranslation } from 'react-i18next';
import { Form } from 'antd';
import { Modal, Typography, Checkbox } from 'antd';
import { Text } from '@/ui/typography';
import { useGetSettings } from '@/features/settings/hooks/useGetSettings';

export const Step1 = ({ goNext }) => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { countries } = useCountries();
  const { showLoader, showInfoToast, showErrorToast, hideLoader } = usePopups();
  const { control, handleSubmit, getValues, setError } = useForm();

  const [isTermsChecked, setTermsChecked] = useState(false);
  const [isEmailTakenModal, setIsEmailTakenModal] = useState(false);

  const { data: settings } = useGetSettings();
  const { data: statics, isLoading } = useGetStatics();

  const privacyPolicyStatics = useMemo(
    () => statics && statics.find((item) => item.title === 'privacy policy'),
    [statics]
  );
  const firstNameLatin = useMemo(() => {
    if (settings) {
      const value = settings.find((item) => item.title === 'firstName validation latin');
      if (value) return JSON.parse(value.text);
    }
    return false;
  }, [settings]);

  const lastNameLatin = useMemo(() => {
    if (settings) {
      const value = settings.find((item) => item.title === 'lastName validation latin');
      if (value) return JSON.parse(value.text);
    }
    return false;
  }, [settings]);

  useEffect(() => {
    if (!state || !state.account) navigate('/registration/investor');
  }, []);

  const checkValidation = (email, phone, account) => {
    return new Promise((resolve, reject) => {
      if (email || phone) {
        investorApi
          .phoneEmailValidation(phone, email.toLowerCase(), account)
          .then((res) => {
            if (res.status === 200) {
              const { data } = res;

              if (!data.validEmail || !data.validPhone) {
                reject('UNIQUE_FAILED');
              }

              resolve();
            } else {
              reject();
            }
          })
          .catch(() => {
            reject();
          });
      }
    });
  };

  const getCitizenshipObj = (citizenship) => {
    return countries.filter((item) => item.id === citizenship)[0];
  };

  const handleNext = async (data) => {
    try {
      showLoader('Processing');

      const { email, phone, password, firstName, lastName, avatar, citizenship } = data;

      await checkValidation(email, phone.replace('/', ''), state.account);

      const citizenshipId = getCitizenshipObj(citizenship);
      if (!citizenshipId || !citizenshipId.id) {
        setError('citizenship', {
          type: 'custom',
          message: t('Citizenship must be selected from the list')
        });
        throw new Error('Citizenship');
      }

      const res = await investorApi.createInvestor({
        email: email.toLowerCase(),
        numberOfSymbolsInCode: phone.split('/')[0].length,
        phone: phone.replace('/', ''),
        password: password,
        account: state.account,
        firstName: firstName,
        lastName: lastName
      });

      if (res.status === 201) {
        const { accessToken, role, ...investor } = res.data;
        localStorage.setItem('token', accessToken);
        localStorage.setItem('userRole', role);
        api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
        dispatch(investorActions.setInvestor(investor));

        goNext();

        await investorApi.setCitizenship(citizenshipId.id);
        if (avatar) await investorApi.uploadAvatar(avatar);

        await kycApi.generateQuestionnaires().then().catch();
      }
    } catch (e) {
      if (e === 'UNIQUE_FAILED') {
        showInfoToast('Email or phone is already taken');
        setIsEmailTakenModal(true);
      } else if (
        e &&
        e.response &&
        e.response.data &&
        e.response.data.message === 'PASSWORD_NO_VALID'
      ) {
        showInfoToast('Password is not valid');
      } else if (
        e &&
        e.response &&
        e.response.data &&
        e.response.data.message === 'NAME_NO_VALID'
      ) {
        showInfoToast('Password is not valid');
      } else {
        showErrorToast(e, true);
      }

      // checkValidation();
    } finally {
      hideLoader();
    }
  };

  return (
    <>
      <Container $maxWidth={600} $wide $marginTop={24}>
        <Form layout='vertical'>
          <FormInput
            form={{
              control,
              name: 'firstName',
              rules: {
                required: t('Required'),
                validate: (v) =>
                  firstNameLatin
                    ? ONLY_LATIN_REGEX.test(v.toLowerCase()) || t('Only latin characters')
                    : CYRILLIC_LATIN_REGEX.test(v.toLowerCase()) ||
                      t('Only latin and cyrillic characters')
              }
            }}
            label={t('First name')}
            maxLength={20}
            required
            boldLabel
          />

          <FormInput
            form={{
              control,
              name: 'lastName',
              rules: {
                required: t('Required'),
                validate: (v) =>
                  lastNameLatin
                    ? ONLY_LATIN_REGEX.test(v.toLowerCase()) || t('Only latin characters')
                    : CYRILLIC_LATIN_REGEX.test(v.toLowerCase()) ||
                      t('Only latin and cyrillic characters')
              }
            }}
            label={t('Last name')}
            maxLength={20}
            required
            marginTopLabel={24}
            boldLabel
          />

          <CountrySelect
            style={{ width: '100%' }}
            label={t('Citizenship')}
            form={{ control, name: 'citizenship', rules: { required: t('Required') } }}
            required
            marginTopLabel={24}
            block
            boldLabel
          />

          <FormInput
            label={t('E-mail')}
            form={{
              control,
              name: 'email',
              rules: {
                required: t('Required'),
                validate: (v) => EMAIL_REGEX.test(v.toLowerCase()) || t('Invalid e-mail address')
              }
            }}
            marginTopLabel={24}
            textTransform={'lowercase'}
            required
            boldLabel
          />

          <PhoneInput
            form={{ control, name: 'phone', rules: { required: t('Required') } }}
            marginTopLabel={24}
            required
            boldLabel
          />

          <FormInput
            form={{
              control,
              name: 'password',
              rules: {
                required: t('Required'),
                pattern: {
                  value: PASSWORD_REGEX,
                  message: t(
                    'Password must be 8–128 characters long. It can include latin letters, numbers, and special characters'
                  )
                },
                deps: ['repeatPassword']
              }
            }}
            marginTopLabel={24}
            label={t(
              'Password (must be 8–128 characters long, it can include latin letters, numbers, and special characters)'
            )}
            required
            password
            boldLabel
          />

          <FormInput
            form={{
              control,
              name: 'repeatPassword',
              rules: {
                required: t('Required'),
                validate: (v) => getValues('password') === v || t('Passwords must match')
              }
            }}
            marginTopLabel={24}
            label={t('Repeat password')}
            required
            password
            boldLabel
          />

          <Flex $marginTop={12} $paddingLeft={2} align={'center'}>
            <Checkbox onChange={(e) => setTermsChecked(e.target.checked)} checked={isTermsChecked}>
              <Text>{t('I agree with')}</Text>
            </Checkbox>

            <Container $marginLeft={'-16'}>
              <Button
                type='link'
                size='large'
                target='_blank'
                href={
                  isLoading || !privacyPolicyStatics
                    ? ''
                    : `${API_URL}/${privacyPolicyStatics.text}`
                }
                text={t('Privacy Policy')}
              />
            </Container>
          </Flex>

          <Flex $marginTop={28}>
            <Container $width={140}>
              <Button
                type='filled'
                size='large'
                text={t('Next')}
                onClick={handleSubmit(handleNext)}
                disabled={!isTermsChecked}
              />
            </Container>
          </Flex>
        </Form>
      </Container>
      <Modal
        open={isEmailTakenModal}
        onCancel={() => setIsEmailTakenModal(false)}
        title={t('Account with this data already exists')}
        okText={'Log in'}
        cancelText={'Cancel'}
        onOk={() => navigate('/login')}
        centered
      >
        <Typography.Paragraph>
          {t('You have already registered with us. Please log in to your account to continue.')}
        </Typography.Paragraph>

        <Link to={'/reset-password'}>{t(`I don't remember my password`)}</Link>
      </Modal>
    </>
  );
};
